import { logout } from './auth';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.API_BASE_URL ||
  'https://backend.wassiniluxury.com/api';

const USER_SERVICE_URL =
  process.env.REACT_APP_USER_SERVICE_URL ||
  process.env.USER_SERVICE_URL ||
  'https://identity.wassiniluxury.com/api';

const buildUrl = (endpoint) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  if (endpoint.startsWith('http')) {
    return endpoint;
  }

  return `${API_BASE_URL}${endpoint}`;
};

// The backend needs the login token for customer data (orders, addresses, favorites, ...)
const authHeaders = () => {
  if (typeof window === 'undefined') return {};
  const token = window.localStorage.getItem('wassiniToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// An old or expired token: drop the session so the next page asks to log in again
const handleUnauthorized = (response) => {
  if (response.status === 401) {
    logout();
  }
};

const fetchJson = async (endpoint, options = {}) => {
  const response = await fetch(buildUrl(endpoint), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    handleUnauthorized(response);
    const error = await response.text();
    let message = error;
    try {
      // The backend explains refusals as { message }, e.g. "Not enough stock for ..."
      message = JSON.parse(error)?.message || error;
    } catch {
      // not JSON, keep the text
    }
    throw new Error(message || 'Failed to fetch');
  }

  return response.json();
};

const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(buildUrl('/saveImage'), {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });

  if (!response.ok) {
    handleUnauthorized(response);
    const error = await response.text();
    throw new Error(error || 'Failed to upload image');
  }

  const json = await response.json();
  return (
    json?.data?.url ||
    json?.url ||
    json?.imageUrl ||
    json?.data ||
    ''
  );
};

const createCustomerProfile = async ({ customerId, firstName, phoneNumber }) => {
  if (!customerId) {
    throw new Error('Customer id is required');
  }
  return fetchJson('/customer', {
    method: 'POST',
    body: JSON.stringify({
      customerId,
      firstName,
      phoneNumber,
    }),
  });
};

const verifyPromoCode = ({ code, customerId }) => {
  const trimmed = String(code || '').trim();
  if (!trimmed) {
    return Promise.reject(new Error('Promo code is required.'));
  }
  return fetchJson(`/checkPromoCode?code=${trimmed}&customer=${customerId}`);
};

const fetchPendingOrders = (customerId) => {
  if (!customerId) {
    return Promise.reject(new Error('Customer id is required to load orders.'));
  }
  return fetchJson(
    `/pendingOrdersByCustomer?customer=${encodeURIComponent(customerId)}`
  );
};

const fetchOrdersByStatus = (customerId, status) => {
  if (!customerId) {
    return Promise.reject(new Error('Customer id is required to load orders.'));
  }
  const params = new URLSearchParams({
    customer: customerId,
    orderStatus: status,
  });
  return fetchJson(`/ordersByCustomerAndStatus?${params.toString()}`);
};

const fetchOrderById = (orderId) => {
  if (!orderId) {
    return Promise.reject(new Error('Order id is required.'));
  }
  return fetchJson(`/order/${encodeURIComponent(orderId)}`);
};

const fetchOrderStatuses = () => fetchJson('/orderStatus');

const fetchFavorites = (customerId) => {
  if (!customerId) {
    return Promise.reject(new Error('Customer id is required to load favorites.'));
  }
  return fetchJson(`/favorite/user?customer=${encodeURIComponent(customerId)}`);
};

const buildFavoritePayload = (favorite = {}) => {
  if (!favorite.customerId) {
    throw new Error('Customer id is required to save favorites.');
  }
  if (!favorite.productId && !favorite.businessId) {
    throw new Error('Either productId or businessId is required to save favorites.');
  }
  return {
    customer: { customerId: favorite.customerId },
    product: favorite.productId ? { productId: favorite.productId } : null,
    business: favorite.businessId ? { restaurant_id: favorite.businessId } : null,
  };
};

const addFavorite = (favorite) => {
  const payload = buildFavoritePayload(favorite);
  return fetchJson('/favorites', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

const deleteFavorite = (favoriteId) => {
  if (!favoriteId) {
    return Promise.reject(new Error('Favorite id is required.'));
  }
  return fetchJson(`/favorites/${encodeURIComponent(favoriteId)}`, {
    method: 'DELETE',
  });
};

const fetchFaqs = () => fetchJson('/faq');
const fetchLabels = () => fetchJson('/labels');

const fetchProductsByCategory = (categoryId, customerId) => {
  if (!categoryId) {
    return Promise.reject(new Error('Category id is required.'));
  }
  const params = new URLSearchParams({ category: categoryId });
  if (customerId) {
    params.append('customer', customerId);
    return fetchJson(`/productByCategoryByCustomer?${params.toString()}`);
  }
  return fetchJson(`/productByCategory?${params.toString()}`);
};

const fetchProductsByBrand = (brandId, customerId) => {
  if (!brandId) {
    return Promise.reject(new Error('Brand id is required.'));
  }
  const params = new URLSearchParams({ brand: brandId });
  if (customerId) {
    params.append('customer', customerId);
    return fetchJson(`/byBrandAndCustomer?${params.toString()}`);
  }
  return fetchJson(`/byBrand?${params.toString()}`);
};

const fetchProducts = (customerId) => {
  if (customerId) {
    return fetchJson(
      `/productsByCustomer?customer=${encodeURIComponent(customerId)}`
    );
  }
  return fetchJson('/products');
};

const fetchFeaturedProducts = (customerId) => {
  if (customerId) {
    return fetchJson(
      `/featuredByCustomer?customer=${encodeURIComponent(customerId)}`
    );
  }
  return fetchJson('/featured');
};

const fetchNewArrivals = (customerId) => {
  if (customerId) {
    return fetchJson(
      `/newArrivalsByCustomer?customer=${encodeURIComponent(customerId)}`
    );
  }
  return fetchJson('/newArrivals');
};

const fetchShipmentStatuses = () => fetchJson('/shipmentStatus');

const fetchOrderTracking = (orderNumber) => {
  const trimmed = String(orderNumber || '').trim();
  if (!trimmed) {
    return Promise.reject(new Error('Order number is required.'));
  }
  return fetchJson(`/track/${encodeURIComponent(trimmed)}`);
};

const buildAddressPayload = (address) => {
  if (!address?.customerId) {
    throw new Error('Customer id is required to save an address.');
  }
  return {
    name: address.name || '',
    street: address.street || '',
    building: address.building || address.locationDescription || '',
    apartment: address.apartment || '',
    city: address.city || '',
    location_description: address.locationDescription || address.building || '',
    isDeleted: false,
    customer: {
      customerId: address.customerId,
    },
    google_map_coordinates: {
      x: Number(address.latitude) || 0,
      z: Number(address.longitude) || 0,
    },
  };
};

export const Api = {
  fetchBanners: () => fetchJson('/banners?position=HOME_SLIDER&platform=WEB'),
  fetchMiddleHomeBanner: () =>
    fetchJson('/banners?position=HOME_MIDDLE_SINGLE&platform=WEB'),
  fetchMiddleDoubleLeftBanner: () =>
    fetchJson('/banners?position=HOME_MIDDLE_DOUBLE_LEFT&platform=WEB'),
  fetchMiddleDoubleRightBanner: () =>
    fetchJson('/banners?position=HOME_MIDDLE_DOUBLE_RIGHT&platform=WEB'),
  fetchCategoriesHeroBanner: () =>
    fetchJson('/banners?position=CATEGORIES_HEADER&platform=WEB'),
  fetchBrands: () => fetchJson('/brand'),
  fetchFeaturedProducts,
  fetchNewArrivals,
  fetchShipmentStatuses,
  fetchOrderTracking,
  fetchProducts,
  fetchCategories: () => fetchJson('/category'),
  fetchProductTypes: () => fetchJson('/typeVariant'),
  sendContactMessage: (payload) =>
    fetchJson('/contact', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  submitInquiry: (payload) =>
    fetchJson('/inquiry', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  fetchCustomerAddresses: (customerId) => {
    if (!customerId) {
      return Promise.reject(new Error('Customer id is required to load addresses.'));
    }
    return fetchJson(`/addressByCustomer?customer=${encodeURIComponent(customerId)}`);
  },
  setDefaultAddress: (addressId, customerId) => {
    if (!addressId || !customerId) {
      return Promise.reject(new Error('Address id and customer id are required.'));
    }
    const params = new URLSearchParams({ customer: customerId });
    return fetchJson(`/address/default/${encodeURIComponent(addressId)}?${params.toString()}`, {
      method: 'PUT',
    });
  },
  addAddress: (address) => {
    const payload = buildAddressPayload(address);
    return fetchJson('/address', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updateAddress: (addressId, address) => {
    if (!addressId) {
      return Promise.reject(new Error('Address id is required to update an address.'));
    }
    const payload = buildAddressPayload(address);
    return fetchJson(`/address/${encodeURIComponent(addressId)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  deleteAddress: (addressId) => {
    if (!addressId) {
      return Promise.reject(new Error('Address id is required to delete an address.'));
    }
    return fetchJson(`/address/${encodeURIComponent(addressId)}`, {
      method: 'DELETE',
    });
  },
  createOrder: (payload) =>
    fetchJson('/order', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  verifyPromoCode,
  fetchPendingOrders,
  fetchOrdersByStatus,
  fetchOrderById,
  fetchOrderStatuses,
  fetchFavorites,
  addFavorite,
  deleteFavorite,
  fetchFaqs,
  fetchLabels,
  fetchProductsByCategory,
  fetchProductsByBrand,
  uploadImage,
  createCustomerProfile,
  API_BASE_URL,
  USER_SERVICE_URL,
};

export default Api;
